/**
 * Unit Preferences Form Component
 * 
 * Comprehensive form for managing user unit preferences including:
 * - Temperature (Celsius/Fahrenheit)
 * - Area (Metric/Imperial)
 * - Precipitation (mm/inches)
 * - Volume (Liters/US Gallons/UK Gallons)
 * - Pressure (Bar/PSI)
 */

import { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  updatePreferencesAsync,
  selectTemperatureUnit,
  selectAreaUnit,
  selectPrecipitationUnit,
  selectVolumeUnit,
  selectPressureUnit,
  selectPreferencesLoading,
  selectPreferencesError,
} from '../../store/slices/preferencesSlice';
import type { AppDispatch } from '../../store';
import type {
  TemperatureUnit,
  AreaUnit,
  PrecipitationUnit,
  VolumeUnit,
  PressureUnit,
} from '../../types/api';

export interface UnitPreferencesFormProps {
  onSaveSuccess?: () => void;
  showTitle?: boolean;
}

/**
 * Unit Preferences Form Component
 * 
 * @example
 * ```tsx
 * <UnitPreferencesForm onSaveSuccess={() => console.log('Saved!')} />
 * ```
 */
export const UnitPreferencesForm: React.FC<UnitPreferencesFormProps> = ({
  onSaveSuccess,
  showTitle = true,
}) => {
  const { t: tCommon } = useTranslation('common', { useSuspense: false });
  const { t: tUnits } = useTranslation('units', { useSuspense: false });
  const dispatch = useDispatch<AppDispatch>();

  // Current preferences from Redux
  const currentTempUnit = useSelector(selectTemperatureUnit);
  const currentAreaUnit = useSelector(selectAreaUnit);
  const currentPrecipUnit = useSelector(selectPrecipitationUnit);
  const currentVolumeUnit = useSelector(selectVolumeUnit);
  const currentPressureUnit = useSelector(selectPressureUnit);
  const loading = useSelector(selectPreferencesLoading);
  const error = useSelector(selectPreferencesError);

  // Local form state
  const [temperatureUnit, setTemperatureUnit] = useState<TemperatureUnit>(currentTempUnit);
  const [areaUnit, setAreaUnit] = useState<AreaUnit>(currentAreaUnit);
  const [precipitationUnit, setPrecipitationUnit] = useState<PrecipitationUnit>(currentPrecipUnit);
  const [volumeUnit, setVolumeUnit] = useState<VolumeUnit>(currentVolumeUnit);
  const [pressureUnit, setPressureUnit] = useState<PressureUnit>(currentPressureUnit);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Check if form has changes
  const hasChanges =
    temperatureUnit !== currentTempUnit ||
    areaUnit !== currentAreaUnit ||
    precipitationUnit !== currentPrecipUnit ||
    volumeUnit !== currentVolumeUnit ||
    pressureUnit !== currentPressureUnit;

  const handleReset = () => {
    setTemperatureUnit(currentTempUnit);
    setAreaUnit(currentAreaUnit);
    setPrecipitationUnit(currentPrecipUnit);
    setVolumeUnit(currentVolumeUnit);
    setPressureUnit(currentPressureUnit);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaveSuccess(false);
      const result = await dispatch(
        updatePreferencesAsync({
          temperature_unit: temperatureUnit,
          area_unit: areaUnit,
          precipitation_unit: precipitationUnit,
          volume_unit: volumeUnit,
          pressure_unit: pressureUnit,
        })
      );

      if (result && 'success' in result && result.success) {
        setSaveSuccess(true);
        onSaveSuccess?.();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveSuccess(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to update unit preferences:', error);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box>
        {showTitle && (
          <Typography variant="h6" gutterBottom>
            {tCommon('settings.unitPreferences')}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {tCommon('settings.preferencesSaved')}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Temperature Unit */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="temperature-unit-label">
                {tCommon('settings.temperatureUnit')}
              </InputLabel>
              <Select
                labelId="temperature-unit-label"
                id="temperature-unit"
                value={temperatureUnit}
                label={tCommon('settings.temperatureUnit')}
                onChange={(e: SelectChangeEvent<TemperatureUnit>) =>
                  setTemperatureUnit(e.target.value as TemperatureUnit)
                }
                disabled={loading}
              >
                <MenuItem value="C">
                  {tUnits('temperature.celsius')} (°C)
                </MenuItem>
                <MenuItem value="F">
                  {tUnits('temperature.fahrenheit')} (°F)
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Area Unit */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="area-unit-label">
                {tCommon('settings.areaUnit')}
              </InputLabel>
              <Select
                labelId="area-unit-label"
                id="area-unit"
                value={areaUnit}
                label={tCommon('settings.areaUnit')}
                onChange={(e: SelectChangeEvent<AreaUnit>) =>
                  setAreaUnit(e.target.value as AreaUnit)
                }
                disabled={loading}
              >
                <MenuItem value="metric">
                  {tUnits('area.metric')} (ha, m²)
                </MenuItem>
                <MenuItem value="imperial">
                  {tUnits('area.imperial')} (acres, ft²)
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Precipitation Unit */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="precipitation-unit-label">
                {tCommon('settings.precipitationUnit')}
              </InputLabel>
              <Select
                labelId="precipitation-unit-label"
                id="precipitation-unit"
                value={precipitationUnit}
                label={tCommon('settings.precipitationUnit')}
                onChange={(e: SelectChangeEvent<PrecipitationUnit>) =>
                  setPrecipitationUnit(e.target.value as PrecipitationUnit)
                }
                disabled={loading}
              >
                <MenuItem value="mm">
                  {tUnits('precipitation.millimeters')} (mm)
                </MenuItem>
                <MenuItem value="inches">
                  {tUnits('precipitation.inches')} (in)
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Volume Unit */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="volume-unit-label">
                {tCommon('settings.volumeUnit')}
              </InputLabel>
              <Select
                labelId="volume-unit-label"
                id="volume-unit"
                value={volumeUnit}
                label={tCommon('settings.volumeUnit')}
                onChange={(e: SelectChangeEvent<VolumeUnit>) =>
                  setVolumeUnit(e.target.value as VolumeUnit)
                }
                disabled={loading}
              >
                <MenuItem value="liters">
                  {tUnits('volume.liters')} (L)
                </MenuItem>
                <MenuItem value="gallons_us">
                  {tUnits('volume.gallonsUS')} (gal US)
                </MenuItem>
                <MenuItem value="gallons_uk">
                  {tUnits('volume.gallonsUK')} (gal UK)
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Pressure Unit */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="pressure-unit-label">
                {tCommon('settings.pressureUnit')}
              </InputLabel>
              <Select
                labelId="pressure-unit-label"
                id="pressure-unit"
                value={pressureUnit}
                label={tCommon('settings.pressureUnit')}
                onChange={(e: SelectChangeEvent<PressureUnit>) =>
                  setPressureUnit(e.target.value as PressureUnit)
                }
                disabled={loading}
              >
                <MenuItem value="bar">
                  {tUnits('pressure.bar')} (bar)
                </MenuItem>
                <MenuItem value="psi">
                  {tUnits('pressure.psi')} (PSI)
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={!hasChanges || loading}
          >
            {tCommon('actions.reset')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!hasChanges || loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? tCommon('actions.saving') : tCommon('actions.save')}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default UnitPreferencesForm;
